import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Box, Button, Heading, HStack, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { getTransactionsRequests } from 'api/transactions';
import { PermissionsV2 } from 'constants/permissions_v2';
import { CREATE_SETTLEMENT_ROUTE } from 'constants/routes';
import { TRANSACTION } from 'constants/transactionDetails';
import TransactionList from 'modules/approve-transactions-v2/components/TransactionList';
import {
  POLICY_EVALUATION_STATUS,
  SMB_TRANSACTION_STATUS_PENDING,
  SMB_TRANSACTION_TYPE_SINGLE_PAYOUT_OPS,
} from 'modules/approve-transactions-v2/constants/constants';
import TransactionRequestDetailsDrawer from 'modules/approve-transactions-v2/TransactionRequestDetailsDrawer';
import { TRANSACTION_LIMIT } from 'modules/merchantinfo/constants/constants';
import PermissionWrapper from 'components/hoc/PermissionWrapper';
import Search from 'components/SearchBox/Search';

export default function ApproveTransactionsV2() {
  const [pendingSmbTransactionRequests, setPendingSmbTransactionRequests] = useState([]);
  const [settlementSmbTransactionRequests, setSettlementSmbTransactionRequests] = useState([]);
  const [approvedSettledSmbTransactionRequests, setApprovedSettledSmbTransactionRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSmbTransactionRequestId, setSelectedSmbTransactionRequestId] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const filteredSmbTransactionRequestList = useMemo(() => {
    const currentList =
      activeTab === 0
        ? pendingSmbTransactionRequests
        : activeTab === 1
        ? settlementSmbTransactionRequests
        : approvedSettledSmbTransactionRequests;

    if (!searchTerm) return currentList;

    return currentList.filter(
      (smbTransaction) =>
        smbTransaction?.TransactionId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        smbTransaction?.SourceAmount?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        smbTransaction?.SourceCurrencyCode?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [pendingSmbTransactionRequests, settlementSmbTransactionRequests, searchTerm, activeTab]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (endIndex < filteredSmbTransactionRequestList?.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const startIndex = (currentPage - 1) * TRANSACTION_LIMIT;
  const endIndex = startIndex + TRANSACTION_LIMIT;
  const visibleSmbTransactionRequestList = filteredSmbTransactionRequestList?.slice(startIndex, endIndex);

  const fetchTransactionRequests = async () => {
    try {
      const today = new Date();
      const yesterday = new Date(today);

      yesterday.setUTCDate(yesterday.getUTCDate() - 30);
      yesterday.setUTCHours(0, 0, 0, 0);
      const startDate = yesterday.toISOString();
      const endDate = today.toISOString();

      setIsLoading(true);
      const [pendingResponse, settlementResponse, approvedSettledResponse] = await Promise.all([
        getTransactionsRequests(SMB_TRANSACTION_STATUS_PENDING, SMB_TRANSACTION_TYPE_SINGLE_PAYOUT_OPS),
        getTransactionsRequests(SMB_TRANSACTION_STATUS_PENDING, SMB_TRANSACTION_TYPE_SINGLE_PAYOUT_OPS, true),
        getTransactionsRequests(
          POLICY_EVALUATION_STATUS.APPROVED,
          SMB_TRANSACTION_TYPE_SINGLE_PAYOUT_OPS,
          true,
          startDate,
          endDate
        ),
      ]);

      setPendingSmbTransactionRequests(pendingResponse?.data?.data ?? []);
      setSettlementSmbTransactionRequests(settlementResponse?.data?.data ?? []);
      setApprovedSettledSmbTransactionRequests(approvedSettledResponse?.data?.data ?? []);
    } catch (error) {
      console.log(error);
      setError(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredSmbTransactionRequestList]);

  useEffect(() => {
    fetchTransactionRequests();
  }, []);

  const totalPages = Math.ceil(filteredSmbTransactionRequestList.length / TRANSACTION_LIMIT);

  const handleTxnClick = (transactionId) => {
    setSelectedSmbTransactionRequestId(transactionId);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedSmbTransactionRequestId('');
  };

  return (
    <Box mt={6} overflowX='auto' maxWidth='100%'>
      <Heading as='h1' size='md' mb={3}>
        Payments
        <Badge ml={1} colorScheme='blue' variant='solid' px={2}>
          {filteredSmbTransactionRequestList?.length}
        </Badge>
      </Heading>

      <HStack justify='space-between' mb={4} px={1}>
        <Search searchType={TRANSACTION} setSearchTerm={setSearchTerm} />
        <PermissionWrapper permission={PermissionsV2.CREATE_PAYMENTS}>
          <Link to={CREATE_SETTLEMENT_ROUTE}>
            <Button colorScheme='blue' size='md'>
              Create Settlement
            </Button>
          </Link>
        </PermissionWrapper>
      </HStack>

      <Tabs isFitted variant='enclosed-colored' isLazy onChange={(index) => setActiveTab(index)}>
        <TabList mt={4}>
          <Tab>Pending Payments</Tab>
          <Tab>Settlement Payments</Tab>
          <Tab>Approved Settlement Payments</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <TransactionList
              transactions={visibleSmbTransactionRequestList}
              isLoading={isLoading}
              error={error}
              currentPage={currentPage}
              totalPages={totalPages}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              isNextDisabled={endIndex >= filteredSmbTransactionRequestList?.length}
              onTransactionClick={handleTxnClick}
              activeTab={activeTab}
            />
          </TabPanel>
          <TabPanel>
            <TransactionList
              transactions={visibleSmbTransactionRequestList}
              isLoading={isLoading}
              error={error}
              currentPage={currentPage}
              totalPages={totalPages}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              isNextDisabled={endIndex >= filteredSmbTransactionRequestList?.length}
              onTransactionClick={handleTxnClick}
              activeTab={activeTab}
            />
          </TabPanel>
          <TabPanel>
            <TransactionList
              transactions={visibleSmbTransactionRequestList}
              isLoading={isLoading}
              error={error}
              currentPage={currentPage}
              totalPages={totalPages}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              isNextDisabled={endIndex >= filteredSmbTransactionRequestList?.length}
              onTransactionClick={handleTxnClick}
              activeTab={activeTab}
              hideApproveButton
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {isDrawerOpen && (
        <TransactionRequestDetailsDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          smbTransactionRequestId={selectedSmbTransactionRequestId}
          refetchPendingTransactions={fetchTransactionRequests}
          isSettlementTransaction={activeTab === 1}
        />
      )}
    </Box>
  );
}
